import { FormControl, FormErrorMessage, Stack } from '@eigtech/ui-shared-dave'
import { ReactNode, useCallback } from 'react'
import { FieldArrayPath, FieldValues, get, useFormState } from 'react-hook-form'
import { BaseProps } from '../base'
import { ArrayFieldContainer } from './ArrayFieldContainer'
import { ArrayFieldProviderProps } from './ArrayFieldContext'
import { ArrayFieldRows, ArrayFieldRowsProps } from './ArrayFieldRows'

export type ArrayFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> = Pick<
  Partial<ArrayFieldRowsProps<TFieldValues, TFieldArrayName>>,
  'renderRow' | 'RowContainer' | 'RowsContainer' | 'emptyMessage'
> &
  Pick<BaseProps, 'control' | 'id'> &
  Pick<ArrayFieldProviderProps<TFieldValues>, 'defaultValue' | 'max' | 'min'> & {
    children?: ReactNode
    label?: string
    name: TFieldArrayName
  }

export function ArrayField<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
>({
  children,
  control,
  emptyMessage,
  renderRow,
  RowContainer,
  RowsContainer,
  ...props
}: ArrayFieldProps<TFieldValues, TFieldArrayName>) {
  const Fallback = useCallback(
    function Fallback() {
      if (renderRow) {
        return (
          <ArrayFieldRows
            RowContainer={RowContainer}
            RowsContainer={RowsContainer}
            emptyMessage={emptyMessage}
            renderRow={renderRow}
          />
        )
      }

      return <></>
    },
    [RowContainer, RowsContainer, emptyMessage, renderRow]
  )

  const { errors } = useFormState({ control })

  const rootError = get(errors, `${props.name}.root`)

  return (
    <FormControl isInvalid={!!rootError}>
      <ArrayFieldContainer {...props}>
        <Stack>{children || <Fallback />}</Stack>
      </ArrayFieldContainer>
      <FormErrorMessage>{rootError?.message}</FormErrorMessage>
    </FormControl>
  )
}

export type ArrayFieldComponent<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> = typeof ArrayField<TFieldValues, TFieldArrayName>
